import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getSolutionTree } from "@/api/solutionApi";
import { getProductCategories, getProducts } from "@/api/productApi";
import { getCaseCategories, getCases } from "@/api/caseApi";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { assetUrl } from "@/utils/assetUrl";

interface NavLink {
  label: string;
  path: string;
  hasDropdown?: boolean;
  dropdownType?: "solutions" | "products" | "cases";
}

const navLinks: NavLink[] = [
  { label: "主页", path: "/" },
  { label: "解决方案", path: "/solutions", hasDropdown: true, dropdownType: "solutions" },
  { label: "产品", path: "/products", hasDropdown: true, dropdownType: "products" },
  { label: "成功案例", path: "/cases", hasDropdown: true, dropdownType: "cases" },
  { label: "关于我们", path: "/about" },
  { label: "行业资讯", path: "/news" },
];

// 三大分类统一配置（文本、图标、颜色）
const L1_CONFIG: Record<string, { name: string; icon: string; accent: string; light: string; desc: string }> = {
  "info-security": { name: "信息安全综合防护", icon: "ri-shield-keyhole-line", accent: "#1a56db", light: "#eff6ff", desc: "防火墙、零信任、SOC等安全防护方案" },
  "it-infrastructure": { name: "IT基础架构", icon: "ri-server-line", accent: "#0891b2", light: "#ecfeff", desc: "数据中心、云计算、超融合等基础架构方案" },
  "intelligent-building": { name: "弱电智能化", icon: "ri-home-gear-line", accent: "#059669", light: "#ecfdf5", desc: "视频监控、门禁、楼宇自控等智能化方案" },
};

// 解决方案图标映射
const solutionIconMap: Record<string, string> = {
  "info-security": "ri-shield-keyhole-line",
  "it-infrastructure": "ri-server-line",
  "intelligent-building": "ri-home-gear-line",
};

// 案例分类颜色（案例的分类id可能不同，做映射）
const caseCategoryColors: Record<string, { accent: string; light: string }> = {
  "info-security": { accent: "#1a56db", light: "#eff6ff" },
  "infrastructure": { accent: "#0891b2", light: "#ecfeff" },
  "smart-integration": { accent: "#059669", light: "#ecfdf5" },
};

// 解决方案分类颜色
const solutionColors: Record<string, { accent: string; light: string }> = {
  "info-security": { accent: "#1a56db", light: "#eff6ff" },
  "it-infrastructure": { accent: "#0891b2", light: "#ecfeff" },
  "intelligent-building": { accent: "#059669", light: "#ecfdf5" },
};

// 产品一级分类颜色
const productL1Colors: Record<string, { accent: string; light: string }> = {
  "info-security": { accent: "#1a56db", light: "#eff6ff" },
  "it-infrastructure": { accent: "#0891b2", light: "#ecfeff" },
  "intelligent-building": { accent: "#059669", light: "#ecfdf5" },
};

// 产品下拉数据：以三大解决方案 L1 为固定分组，
// 通过 subSolution.relatedProductIds → 产品 → 产品.category（即产品类型 id）
// 组装出：{ [l1]: [{ typeId, typeName, products: Product[] }...] }
interface ProductTypeGroup {
  typeId: string;
  typeName: string;
  products: { id: number; name: string }[];
}

const useProductsByL1 = (enabled: boolean) => {
  const [map, setMap] = useState<Record<string, ProductTypeGroup[]>>({});

  useEffect(() => {
    if (!enabled) return;
    Promise.all([
      getProductCategories(),
      getProducts(),
      getSolutionTree(),
    ])
      .then(([catRes, products, tree]: any[]) => {
        const l1s = (catRes?.l1Categories || []).filter(
          (l1: any) => (l1.id ?? l1.categoryId) !== "all",
        );
        // 产品类型 id → 名称
        const typeIdToName = new Map<string, string>();
        l1s.forEach((l1: any) => {
          const key = String(l1.categoryId ?? l1.id);
          typeIdToName.set(key, l1.name);
        });

        // 产品 id → 产品
        const productById = new Map<number, any>();
        (products || []).forEach((p: any) => productById.set(Number(p.id), p));

        // 解决方案 L1（info-security/it-infrastructure/intelligent-building） → 关联的产品 id 集合
        const l1ToProductIds: Record<string, Set<number>> = {
          "info-security": new Set(),
          "it-infrastructure": new Set(),
          "intelligent-building": new Set(),
        };
        (tree || []).forEach((cat: any) => {
          const l1Key = cat.categoryId ?? cat.id;
          const bucket = l1ToProductIds[l1Key];
          if (!bucket) return;
          (cat.subSolutions || []).forEach((sub: any) => {
            const ids: any[] = sub.relatedProductIds ?? sub.relatedProducts ?? [];
            ids.forEach((id: any) => {
              const n = Number(id);
              if (!Number.isNaN(n)) bucket.add(n);
            });
          });
        });

        // 按 L1 → 产品类型 分组
        const result: Record<string, ProductTypeGroup[]> = {};
        Object.entries(l1ToProductIds).forEach(([l1Key, idSet]) => {
          const groupByType = new Map<string, ProductTypeGroup>();
          idSet.forEach((pid) => {
            const product = productById.get(pid);
            if (!product) return;
            const typeId = String(product.category ?? "");
            if (!typeId) return;
            if (!groupByType.has(typeId)) {
              groupByType.set(typeId, {
                typeId,
                typeName: typeIdToName.get(typeId) || typeId,
                products: [],
              });
            }
            groupByType.get(typeId)!.products.push({ id: Number(product.id), name: product.name });
          });
          result[l1Key] = Array.from(groupByType.values());
        });
        setMap(result);
      })
      .catch(() => {});
  }, [enabled]);

  return map;
};

// 获取案例分类数据（从API加载）
const useCaseCategories = (enabled: boolean) => {
  const [caseCategories, setCaseCategories] = useState<any[]>([]);
  useEffect(() => {
    if (!enabled) return;
    Promise.all([getCaseCategories(), getCases({ pageNum: 1, pageSize: 50 })]).then(([catRes, casesRes]: any[]) => {
      const cats = (Array.isArray(catRes) ? catRes : []).filter((c: any) => c.id !== "all");
      const allCases = casesRes.rows || [];
      setCaseCategories(cats.map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        cases: allCases.filter((c: any) => c.solution === cat.id).slice(0, 5),
      })));
    }).catch(() => {});
  }, [enabled]);
  return caseCategories;
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [solutionCategories, setSolutionCategories] = useState<any[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // 下拉数据懒加载开关：用户首次 hover 对应菜单后才置为 true，避免首屏拉取
  const [productsTouched, setProductsTouched] = useState(false);
  const [casesTouched, setCasesTouched] = useState(false);
  const [solutionsTouched, setSolutionsTouched] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const productsByL1 = useProductsByL1(productsTouched);
  const caseCategories = useCaseCategories(casesTouched);
  const companyInfo = useCompanyInfo();

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isHomePage = location.pathname === "/";

  // 解决方案下拉数据：仅在用户首次 hover「解决方案」菜单后加载
  useEffect(() => {
    if (!solutionsTouched) return;
    getSolutionTree().then((res: any) => {
      const data = Array.isArray(res) ? res : [];
      setSolutionCategories(data);
      // 默认展开全部分类
      const allIds = new Set(data.map((s: any) => s.categoryId || s.id));
      setExpandedCategories(allIds);
    }).catch(() => {});
  }, [solutionsTouched]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = isHomePage ? window.innerHeight * 0.85 : 60;
      setScrolled(window.scrollY > scrollThreshold);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path) && path !== "/";
  };

  const isTransparent = isHomePage && !scrolled;

  const handleMouseEnter = (dropdownType: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // 首次 hover 时触发对应下拉数据的懒加载
    if (dropdownType === "solutions") setSolutionsTouched(true);
    else if (dropdownType === "products") setProductsTouched(true);
    else if (dropdownType === "cases") setCasesTouched(true);
    setActiveDropdown(dropdownType);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
    // 重置为全部展开
    const allIds = new Set(solutionCategories.map((s: any) => s.categoryId || s.id));
    setExpandedCategories(allIds);
  };

  // ── 解决方案下拉菜单 ──────────────────────────────────────────────────────
  const renderSolutionsDropdown = () => (
    <div
      className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
      style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
      onMouseEnter={handleDropdownMouseEnter}
      onMouseLeave={handleDropdownMouseLeave}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* 顶部标题行 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Solutions</p>
            <h3 className="text-base font-bold text-[#0d1f3c]">三大核心解决方案</h3>
          </div>
          <Link
            to="/solutions"
            onClick={closeDropdown}
            className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
          >
            查看全部方案
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* 三列内容 */}
        <div className="grid grid-cols-3 gap-5 items-start">
          {solutionCategories.map((solution) => {
            const cfg = L1_CONFIG[solution.categoryId || solution.id];
            const color = solutionColors[solution.categoryId || solution.id] || { accent: "#1a56db", light: "#eff6ff" };
            const displayName = cfg?.name || solution.title;
            const displayIcon = cfg?.icon || solutionIconMap[solution.id] || "ri-settings-line";
            const catId = solution.categoryId || solution.id;
            const isExpanded = expandedCategories.has(catId);
            const subSolutions: any[] = solution.subSolutions || [];
            return (
              <div key={solution.id} className="group">
                {/* 大类标题卡片 */}
                <Link
                  to={`/solutions?category=${catId}`}
                  onClick={closeDropdown}
                  className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
                    style={{ backgroundColor: color.light }}
                  >
                    <i className={`${displayIcon} text-base`} style={{ color: color.accent }}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{displayName}</span>
                      <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                    </div>
                    {cfg?.desc && <p className="text-xs text-gray-400 truncate mt-0.5">{cfg.desc}</p>}
                  </div>
                </Link>

                {/* 分割线 */}
                <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${color.accent}15` }}></div>

                {/* 子方案列表 - 两列布局，默认最多6条（3行×2列） */}
                <div className="px-1">
                  <div className="grid grid-cols-2 gap-x-2">
                    {(isExpanded ? subSolutions : subSolutions.slice(0, 6)).map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/solutions/detail/${sub.id}`}
                        onClick={closeDropdown}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-[#0d1f3c] hover:bg-gray-50 transition-all duration-150 group/item whitespace-nowrap overflow-hidden"
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-150 group-hover/item:scale-125"
                          style={{ backgroundColor: color.accent, opacity: 0.4 }}
                        ></span>
                        <span className="truncate">{sub.name}</span>
                      </Link>
                    ))}
                  </div>

                  {subSolutions.length > 6 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(catId);
                      }}
                      className="w-full flex items-center justify-center gap-1 mt-2 py-1.5 text-xs text-gray-400 hover:text-[#1a56db] transition-colors cursor-pointer"
                    >
                      {isExpanded ? (
                        <>
                          收起
                          <i className="ri-arrow-up-s-line text-sm"></i>
                        </>
                      ) : (
                        <>
                          展开全部（共{subSolutions.length}个）
                          <i className="ri-arrow-down-s-line text-sm"></i>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── 产品下拉菜单（一级固定为三大解决方案）─────────────────────────────────
  const SOLUTION_L1 = [
    { id: "info-security", name: "信息安全综合防护", icon: "ri-shield-keyhole-line" },
    { id: "it-infrastructure", name: "IT基础架构", icon: "ri-server-line" },
    { id: "intelligent-building", name: "弱电智能化", icon: "ri-home-gear-line" },
  ];

  const renderProductsDropdown = () => {
    return (
      <div
        className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          {/* 顶部标题行 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Products</p>
              <h3 className="text-base font-bold text-[#0d1f3c]">全系列产品</h3>
            </div>
            <Link
              to="/products"
              onClick={closeDropdown}
              className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
            >
              查看全部产品
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 三列内容 — 固定三大解决方案 */}
          <div className="grid grid-cols-3 gap-5">
            {SOLUTION_L1.map((solution) => {
              const color = productL1Colors[solution.id] || { accent: "#1a56db", light: "#eff6ff" };
              const groups = productsByL1[solution.id] || [];

              return (
                <div key={solution.id}>
                  {/* 解决方案标题 */}
                  <Link
                    to={`/products?solution=${solution.id}`}
                    onClick={closeDropdown}
                    className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50 group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color.light }}
                    >
                      <i className={`${solution.icon} text-base`} style={{ color: color.accent }}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{solution.name}</span>
                        <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                      </div>
                      {L1_CONFIG[solution.id]?.desc && <p className="text-xs text-gray-400 truncate mt-0.5">{L1_CONFIG[solution.id].desc}</p>}
                    </div>
                  </Link>

                  {/* 分割线 */}
                  <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${color.accent}15` }}></div>

                  {/* 产品类型 + 每类型下的产品 */}
                  <div className="space-y-2 px-1">
                    {groups.length === 0 && (
                      <p className="px-2 py-1 text-xs text-gray-300">暂无产品</p>
                    )}
                    {groups.slice(0, 5).map((g) => (
                      <div key={g.typeId}>
                        <Link
                          to={`/products?solution=${solution.id}&category=${g.typeId}`}
                          onClick={closeDropdown}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-gray-600 hover:text-[#0d1f3c] transition-colors rounded-md hover:bg-gray-50"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color.accent }}
                          ></span>
                          {g.typeName}
                        </Link>
                        {g.products.length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-5 mt-0.5">
                            {g.products.slice(0, 4).map((p) => (
                              <Link
                                key={p.id}
                                to={`/products/${p.id}`}
                                onClick={closeDropdown}
                                className="text-xs text-gray-400 hover:text-[#1a56db] transition-colors py-0.5 truncate max-w-[180px]"
                                title={p.name}
                              >
                                {p.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── 案例下拉菜单 ──────────────────────────────────────────────────────────
  const renderCasesDropdown = () => {
    return (
      <div
        className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          {/* 顶部标题行 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Cases</p>
              <h3 className="text-base font-bold text-[#0d1f3c]">成功案例</h3>
            </div>
            <Link
              to="/cases"
              onClick={closeDropdown}
              className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
            >
              查看全部案例
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 三列内容 */}
          <div className="grid grid-cols-3 gap-5">
            {caseCategories.map((category) => {
              // 案例分类id到L1_CONFIG的映射
              const l1Map: Record<string, string> = { "info-security": "info-security", "infrastructure": "it-infrastructure", "smart-integration": "intelligent-building" };
              const l1Key = l1Map[category.id] || category.id;
              const cfg = L1_CONFIG[l1Key];
              const color = caseCategoryColors[category.id] || { accent: cfg?.accent || "#1a56db", light: cfg?.light || "#eff6ff" };
              const displayName = cfg?.name || category.name;
              const displayIcon = cfg?.icon || "ri-briefcase-4-line";
              return (
                <div key={category.id}>
                  {/* 大类标题 */}
                  <Link
                    to={`/cases?solution=${category.id}`}
                    onClick={closeDropdown}
                    className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50 group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color.light }}
                    >
                      <i className={`${displayIcon} text-base`} style={{ color: color.accent }}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{displayName}</span>
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: color.light, color: color.accent }}
                        >
                          {category.cases.length}
                        </span>
                        <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                      </div>
                      {cfg?.desc && <p className="text-xs text-gray-400 truncate mt-0.5">{cfg.desc}</p>}
                    </div>
                  </Link>

                  {/* 分割线 */}
                  <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${color.accent}15` }}></div>

                  {/* 案例列表 */}
                  <ul className="space-y-0.5 px-1">
                    {category.cases.map((caseItem) => (
                      <li key={caseItem.id}>
                        <Link
                          to={`/cases/${caseItem.id}`}
                          onClick={closeDropdown}
                          className="flex items-start gap-2 px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-[#0d1f3c] hover:bg-gray-50 transition-all duration-150 group/item"
                        >
                          <span
                            className="w-1 h-1 rounded-full flex-shrink-0 mt-1.5 transition-all duration-150 group-hover/item:scale-150"
                            style={{ backgroundColor: color.accent, opacity: 0.4 }}
                          ></span>
                          <span className="line-clamp-1">{caseItem.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        isTransparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-md border-b border-gray-100/50 shadow-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          {(isTransparent ? (companyInfo.logoDark || companyInfo.logo) : companyInfo.logo) ? (
            <img
              src={assetUrl(isTransparent ? (companyInfo.logoDark || companyInfo.logo) : companyInfo.logo)}
              alt={companyInfo.companyName}
              className="h-8 max-w-[160px] object-contain"
            />
          ) : (
            <>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-500 ${
                  isTransparent ? "bg-white/20" : "bg-[#1a56db]"
                }`}
              >
                <i className="ri-shield-check-line text-lg text-white"></i>
              </div>
              <span className={`text-sm font-bold hidden sm:inline transition-colors duration-500 ${isTransparent ? "text-white" : "text-[#0d1f3c]"}`}>
                {companyInfo.companyName}
              </span>
            </>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center justify-center absolute left-1/2 -translate-x-1/2 gap-1">
          {navLinks.map((link) => (
            <div
              key={link.path}
              className="relative"
              onMouseEnter={() => link.hasDropdown && link.dropdownType && handleMouseEnter(link.dropdownType)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to={link.path}
                className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                  isActive(link.path)
                    ? isTransparent
                      ? "text-white bg-white/20"
                      : "text-[#1a56db] bg-[#1a56db]/8"
                    : isTransparent
                      ? "text-white hover:text-white hover:bg-white/10"
                      : "text-[#333333] hover:text-[#1a56db] hover:bg-[#1a56db]/5"
                }`}
              >
                {link.label}
                {link.hasDropdown && (
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                      activeDropdown === link.dropdownType ? "rotate-180" : ""
                    }`}
                  ></i>
                )}
              </Link>
            </div>
          ))}
        </nav>

        {/* CTA Button（暂时隐藏，改用爱番番第三方客服） */}
        {/* <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => window.dispatchEvent(new Event("open-contact-hub"))}
            className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isTransparent
                ? "bg-white text-[#0d1f3c] hover:bg-white/90 shadow-lg"
                : "bg-[#1a56db] hover:bg-[#1e40af] text-white shadow-md hover:shadow-lg"
            }`}
          >
            免费咨询
          </button>
        </div> */}

        {/* Mobile Menu Button */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
            isTransparent ? "text-white" : "text-[#333333]"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Dropdown Menus */}
      <div>
        {activeDropdown === "solutions" && renderSolutionsDropdown()}
        {activeDropdown === "products" && renderProductsDropdown()}
        {activeDropdown === "cases" && renderCasesDropdown()}
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`block py-3 text-sm border-b border-gray-50 last:border-0 ${
                isActive(link.path) ? "text-[#1a56db] font-medium" : "text-[#333333] hover:text-[#1a56db]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {/* 免费咨询按钮暂时隐藏，改用爱番番第三方客服 */}
          {/* <button
            onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("open-contact-hub")); }}
            className="mt-4 block w-full text-center px-5 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer"
          >
            免费咨询
          </button> */}
        </div>
      )}
    </header>
  );
}
